import {
  type AutocompleteRenderInputParams,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import {
  type ChangeEventHandler,
  type FC,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  useMemo,
  useState,
} from 'react';

import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

interface Props {
  label?: string;
  name?: string;
  value: string;
  onChange:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  type?: string;
  helperText?: string;
  errorText?: string;
  icon?: ReactNode;
  placeholder?: string;
  fullWidth?: boolean;
  doBlur?: boolean;
  lighterError?: boolean;
  emptyHelperHeight?: boolean;
  autocompleteParams?: AutocompleteRenderInputParams;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
}

const CustomTextField: FC<Props> = ({
  label,
  name,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  type,
  helperText,
  errorText,
  icon,
  placeholder,
  fullWidth,
  doBlur = false,
  emptyHelperHeight = false,
  autocompleteParams,
  disabled = false,
  multiline = false,
  rows = 4,
}) => {
  const [focused, setFocused] = useState(false);
  const [blurred, setBlurred] = useState(doBlur);

  const errorColor = '#D4183D';

  const highlightError = useMemo(
    () => !!errorText && blurred,
    [errorText, blurred]
  );

  return (
    <Box sx={fullWidth ? { width: '100%' } : {}}>
      <Box
        sx={{
          padding: '8px 16px',
          backgroundColor: highlightError
            ? color.grey['40']
            : focused
              ? color.grey['40']
              : color.grey['20'],
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: multiline ? 'flex-start' : 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                marginTop: multiline ? '24px' : 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Typography
              className="label"
              sx={{
                marginBottom: '4px',
                ...typography.caption,
                fontWeight: 350,
                color: highlightError
                  ? color.charcoal[400]
                  : focused
                    ? color.charcoal[400]
                    : color.charcoal[400],
              }}
            >
              {label}
            </Typography>
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onFocus={(event) => {
                onFocus?.(event);
                setFocused(true);
              }}
              onBlur={(event) => {
                onBlur?.(event);
                setBlurred(true);
                setFocused(false);
              }}
              type={type}
              placeholder={placeholder}
              fullWidth
              error={highlightError}
              disabled={disabled}
              multiline={multiline}
              rows={multiline ? rows : undefined}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-root': {
                  outline: 'none',
                },
                input: {
                  '&::-ms-reveal': { display: 'none' },
                  outline: 'none',
                },

                '& .MuiInputBase-input': {
                  padding: '0 !important',
                  resize: multiline ? 'vertical' : 'none',
                  fontFamily: 'Gotham',
                  fontWeight: 325,
                },

                '& .MuiInputBase-inputMultiline': {
                  padding: '0 !important',
                  resize: 'vertical',
                },

                '& .MuiInputBase-root.MuiInputBase-multiline': {
                  padding: '0 !important',
                },

                '& .MuiInputBase-root': {
                  backgroundColor: 'transparent',
                  alignItems: 'flex-start',

                  '&.Mui-focused': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-error': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-filled': {
                    backgroundColor: 'transparent',
                  },
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px transparent inset ',
                    backgroundColor: 'transparent ',
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                    {
                      WebkitBoxShadow: '0 0 0 1000px transparent inset ',
                      backgroundColor: 'transparent ',
                      WebkitTextFillColor: `${color.charcoal[500]} `,
                    },
                  '& input:-internal-autofill-selected, & input:-internal-autofill-previewed':
                    {
                      backgroundColor: 'transparent ',
                      WebkitBoxShadow: '0 0 0 1000px transparent inset',
                      WebkitTextFillColor: `${color.charcoal[500]} `,
                      transition:
                        'background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s',
                    },
                },
              }}
              {...autocompleteParams}
            />
          </Box>
        </Box>
      </Box>
      <Typography
        color={highlightError ? errorColor : color.charcoal[400]}
        variant="overline"
        mt={0.5}
        sx={{
          userSelect: highlightError || helperText ? 'auto' : 'none',
          pointerEvents: highlightError || helperText ? 'auto' : 'none',
          textTransform: 'none',
        }}
      >
        {highlightError && errorText
          ? errorText
          : (helperText ?? (emptyHelperHeight ? '\u00A0' : undefined))}
      </Typography>
    </Box>
  );
};

export default CustomTextField;
